import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { DISABLED, FOCUS_RING } from "@/components/ui/focusRing"

// ─────────────────────────────────────────────────────────────────────────────
// Canonical variants (Phase 2 locked standard):
//
//   • secondary  — DEFAULT. Surface + 1px hairline border. The workhorse.
//   • primary    — Bone/ink fill + primary-foreground text (dark-on-bone in light,
//                  bone-on-ink in dark). Primary action per surface.
//   • ghost      — Transparent + fg-secondary. Borderless nav/inline actions.
//   • destructive — Danger fill. Delete / hard error actions.
//
// Legacy aliases (kept for build safety; the 02-06 sweep will migrate callers):
//   • default    — alias for secondary
//   • cta        — alias for primary
//   • accent     — alias for primary
//   • glass      — alias for ghost
//   • outline    — alias for secondary
//
// Design rules: transition-colors only, no transform on hover, no glow/shadow,
// no resting shadow, no press depth. A button has no volume.
//
// TYPE: 13px at weight 500 (`text-control font-medium`), not 14px semibold.
// Weight is most of why the chrome used to read heavier than the app it frames,
// and the reference this is measured against uses `font-bold` zero times, so
// the `lg` size no longer steps up to bold either.
//
// CORNER: `rounded-lg` (8px) at every size except `sm`, which is `rounded-md`
// (6px). This used to be `rounded-xl` (12px), which put a button and the input
// beside it at the same 36px height on different corners.
// ─────────────────────────────────────────────────────────────────────────────
const buttonVariants = cva(
  cn(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap text-control font-medium",
    // THE PRESS TIMING, and it is asymmetric on purpose.
    //
    // A measured touchscreen tap is 133ms finger-down to finger-up. Sampled per
    // frame over a real 40ms tap, a symmetric 160ms colour transition travels
    // 10% of its own step and a symmetric 100ms travels 17%. The shipped base
    // here was `duration-200`, so a press written symmetrically would be
    // correct in the stylesheet and absent on the screen.
    //
    // `active:duration-0` holds `transition-duration: 0s` for as long as
    // `:active` does, so the fill ARRIVES instantly; on release the `:active`
    // rule stops applying, the base 150ms returns, and the fill decays. That is
    // Linear's own `--speed-highlightFadeIn: 0s` / `--speed-highlightFadeOut:
    // .15s` pair, written in Tailwind. 100% of the step on any tap, and off
    // rest for 167ms minimum.
    //
    // `transition-colors` and never `transition-all`: Tailwind v4 expands it to
    // an explicit list (color, background-color, border-color, outline-color,
    // text-decoration-color, fill, stroke, gradient stops) with no transform, no
    // box-shadow and no opacity in it. Nothing here can move.
    //
    // NO `motion-reduce:` BRANCH, and that is the result rather than an
    // omission. WCAG 2.3.3 covers size and position changes and explicitly
    // excludes "changes of color ... that do not alter the perceived size,
    // shape, or position". This press admits no channel 2.3.3 covers, so every
    // user gets the identical press and there is no conditional to get wrong.
    "transition-colors duration-150 ease-out active:duration-0",
    // Under `forced-colors: active` the background press is ERASED: measured in
    // Chromium, the button computes rgb(255,255,255) both at rest and pressed,
    // because the UA re-sources every colour. System colour keywords are the one
    // source it honours, so the press is re-sourced rather than lost. Measured
    // to work, and measured NOT to need `forced-color-adjust: none`.
    "forced-colors:active:bg-[Highlight] forced-colors:active:text-[HighlightText]",
    FOCUS_RING,
    DISABLED,
    "disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  ),
  {
    variants: {
      variant: {
        // Canonical: surface + hairline border (default workhorse).
        // NOTE: this said `border-strong` until 2026-07-24, but bare
        // `border-strong` is not a generated utility — it emitted nothing and
        // the base layer's `* { @apply border-border }` supplied the hairline.
        // Renamed to the class that was actually painting: zero visual change.
        secondary:
          "bg-surface border border-border text-fg hover:bg-elevated active:bg-surface-active",
        // Canonical: bone/ink fill (primary action per surface)
        primary:
          "bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-active",
        // Canonical: transparent + fg-secondary (nav, inline)
        ghost:
          "text-fg-secondary hover:text-fg hover:bg-surface-hover active:bg-surface-active",
        // Canonical: danger fill (delete, hard errors)
        destructive:
          "bg-danger text-danger-on hover:bg-danger-hover active:bg-danger-active",

        // Legacy aliases — kept for build safety until 02-06 sweep migrates callers
        default:
          "bg-surface border border-border text-fg hover:bg-elevated active:bg-surface-active",
        cta:
          "bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-active",
        accent:
          "bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-active",
        glass:
          "text-fg-secondary hover:text-fg hover:bg-surface-hover active:bg-surface-active",
        outline:
          "bg-surface border border-border text-fg hover:bg-elevated active:bg-surface-active",
      },
      size: {
        sm: "h-8 px-3 text-xs rounded-md",
        default: "h-9 px-4 rounded-lg",
        lg: "h-10 px-6 rounded-lg",
        icon: "h-9 w-9 rounded-lg",
        "icon-lg": "h-10 w-10 rounded-lg",
        // A Button whose geometry belongs to the call site. Emits no height, no
        // inline padding and no radius, so a row or card recipe can compose on
        // top cleanly. The base recipe's focus contract is preserved for free.
        cell: "h-auto w-full p-0 rounded-none whitespace-normal font-normal",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
